import { SagaIterator } from 'redux-saga';
import { call, delay, race, take, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  getAngebotsIds,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  ShopTarifierungRequestEvents,
} from '@ps20-frontend/shop-integration-redux';
import { handleTarifierungsReturnCodeInShop } from '@ps20-frontend/preis-lib';
import { PS20Logger } from '@ps20-frontend/logging';
import { App } from '@ps20-frontend/frontend-models';
import {
  latestStateSnapshotTrigger,
  offersToRestore,
  StateSnapshotTrigger,
} from '../stateSnapshotTrigger';
import { addTaasLoadingActions } from './addTaasLoadingActions.util';

export function* watchTarifiereShop(): SagaIterator {
  yield takeLatest(GlobalActions.TARIFIERE_SHOP, tarifiereShop);
  yield takeLatest(GlobalActions.TARIFIERE_SHOP_SYNC, handleTarifiereShopSync);
}

export function* handleTarifiereShopSync(action: GlobalAction<GlobalAction<unknown>>): SagaIterator {
  const handleTarifiere = addTaasLoadingActions(tarifiereShopSync);
  yield call(handleTarifiere, action);
}

export function* tarifiereShop(action: GlobalAction<GlobalAction<unknown>>): SagaIterator {
  const globalPut = GlobalPut.getInstance();

  yield call(globalPut.put, new GlobalActions().requestAngeboteInWarenkorb());
  const angeboteInWarenkorbAction: GlobalAction<Array<AngebotsId>> = yield take(
    GlobalActions.ANGEBOTE_IN_WARENKORB_PROVIDED,
  );
  const angeboteInWarenkorb = angeboteInWarenkorbAction.payload;

  // Damit die States von allen Apps aktualisiert sind
  yield delay(0);
  for (const angebot of angeboteInWarenkorb) {
    yield call(
      globalPut.put,
      new GlobalActions().tarifiereAngebotForShop(angebot.appId, angebot.angebotsId),
    );
  }

  let errorAction = null;

  for (const _appId of angeboteInWarenkorb) {
    const taasActions = yield race(
      Object.values(ShopTarifierungRequestEvents).map(actionType => take(actionType)),
    );
    const taasAction = taasActions.find((_action: unknown) => _action != null);
    if (
      taasAction &&
      taasAction.type === ShopTarifierungRequestEvents.PREISE_SHOP_REQUESTED_FAILED
    ) {
      errorAction = taasAction;
    }
  }

  if (errorAction) {
    const taasStatus = errorAction.payload.response.data.taasStatus;
    const originEvent = action.payload;

    const snapshotTrigger: StateSnapshotTrigger | null = yield call(latestStateSnapshotTrigger);
    if (originEvent.type !== GlobalActions.TARIFIERUNG_TRIGGERED_BEIBEHALTUNG) {
      if (snapshotTrigger?.skipsRollback) {
        PS20Logger.info(
          'tarifiereShop.stateRollbackSkipped',
          JSON.stringify({
            trigger: snapshotTrigger.type,
            skipsRollback: snapshotTrigger.skipsRollback,
          }),
          App.SHOP,
        );
        yield call(globalPut.put, new GlobalActions().tarifiereShopStale());
      } else {
        yield call(restoreStateSnapshots, snapshotTrigger);
      }
    }

    yield call(handleTarifierungsReturnCodeInShop, taasStatus, originEvent);
    yield call(globalPut.put, new GlobalActions().tarifiereShopFailed());
  } else {
    yield call(globalPut.put, new GlobalActions().tarifiereShopSuccess());
  }
}

function* restoreStateSnapshots(snapshotTrigger: StateSnapshotTrigger | null): SagaIterator {
  const registeredOffers = yield call(getAngebotsIds);
  for (const angebotsId of offersToRestore(registeredOffers, snapshotTrigger)) {
    yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().restoreStateSnapshot(angebotsId.appId, angebotsId.angebotsId),
    );
    yield take(GlobalActions.RESTORE_STATE_SNAPSHOT_FINISHED);
  }
}

export function* tarifiereShopSync(action: GlobalAction<unknown>): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  yield call(globalPut.put, new GlobalActions().tarifiereShop(action));

  yield race({
    success: take(GlobalActions.TARIFIERE_SHOP_SUCCESS),
    failed: take(GlobalActions.TARIFIERE_SHOP_FAILED),
  });
}
